import type { PropsWithChildren } from 'react';
import { router } from '@inertiajs/react';
import { useEffect } from 'react';
import { syncFromUserLocale } from '@/i18n';

/** Structural stand-in for Inertia's Page — we only ever read `props`. */
type PageLike = { props: Record<string, unknown> };

type Shared = { auth?: { user?: { locale?: string | null } } };

const localeOf = (props: Record<string, unknown> | undefined): string | null =>
  ((props ?? {}) as Shared).auth?.user?.locale ?? null;

/**
 * Outer shell. Owns the global providers and (later) the top bar + left rail.
 * Pages get to render into the main slot.
 *
 * NOTE: AppShell sits OUTSIDE Inertia's <App> provider (see main.tsx), so
 * we can't call `usePage()` here. main.tsx hands us the initial page instead,
 * and we re-sync from the event payload on every successful navigation.
 */
export function AppShell({ initialPage, children }: PropsWithChildren<{ initialPage?: PageLike }>) {
  useI18nSync(initialPage);
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* TODO: top bar, command palette, left nav, tenant switcher, user menu */}
      {children}
    </div>
  );
}

/**
 * Phase 7aa — keep i18next in sync with `auth.user.locale` from Inertia.
 * Seeds from the initial page handed down by main.tsx, then re-reads from the
 * `success` event payload after every navigation lands. Works above the
 * Inertia React context — no usePage() required.
 */
function useI18nSync(initialPage?: PageLike) {
  useEffect(() => {
    syncFromUserLocale(localeOf(initialPage?.props));       // initial sync
    return router.on('success', (event) =>
      syncFromUserLocale(localeOf(event.detail.page.props)));  // every nav
  }, [initialPage]);
}
